import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions
} from '@floating-ui/react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import ProgressIndicator from 'components/common/progress-indicator';
import Text from 'components/inputs/text';
import XButton from 'components/inputs/x-button';
import SystemMap from 'components/systems/system-map';
import { SelectedSystemType } from 'data/types';
import { useDeleteQuotableSystemMutation } from 'features/api/project-api';
import {
  setActiveSelectedSystem,
  setActiveSystemType,
  setSelectedSystems,
  updateSelectedSystem
} from 'features/systems';
import { useIsSystemQuoted } from 'hooks/use-quoted-unique-ids';
import { produce } from 'immer';
import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { showsAccessoryChipConfigurationName } from 'utils/accessory-system-chip';
import {
  ductedDuplicateNameIndexes,
  nameWithDuplicateIndex
} from 'utils/duplicate-system-names';
import { confirmationModal, warningModal } from 'utils/ui';

type SystemOptionProps = {
  system: SelectedSystemType;
  isAccessoryPage?: boolean;
  hideDeletable?: boolean;
  hideRenamable?: boolean;
};

export default function SelectedSystemComponent({
  system,
  isAccessoryPage = false,
  hideDeletable,
  hideRenamable
}: SystemOptionProps) {
  const dispatch = useAppDispatch();
  const {
    selectedSystems,
    activeSelectedSystem,
    selectedDuctedSystems,
    activeSystemType,
    quotableSystems
  } = useAppSelector((state) => state.systems);
  const [showPreview, setShowPreview] = useState(false);
  const [active, setActive] = useState(false);
  const [attentionNeeded, setAttentionNeeded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [deleteQuotableSystem] = useDeleteQuotableSystemMutation();
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const renameSizeLimit = 25;
  const isSystemQuoted = useIsSystemQuoted();
  const isQuoted = isSystemQuoted(system);

  const handleDoubleClickName = () => {
    if (!hideRenamable) {
      setRenaming(true);
      setNewName(system.name);
    }
  };

  const handleSaveName = () => {
    const trimmed = newName.trim();
    if (trimmed !== '' && trimmed !== system.name) {
      dispatch(
        updateSelectedSystem({ uniqueId: system.uniqueId, name: trimmed })
      );
    }
    setRenaming(false);
  };

  const handleRenameKeyDown = (key: string) => {
    if (key === 'Enter') {
      handleSaveName();
    } else if (key === 'Escape') {
      setRenaming(false);
    }
  };

  useEffect(() => {
    setActive(
      activeSystemType === 'ductless' &&
        selectedSystems.findIndex((sys) => sys.uniqueId === system.uniqueId) ===
          activeSelectedSystem
    );
    const accessoriesReviewed =
      quotableSystems[system.uniqueId]?.accessoriesReviewed ?? false;
    setAttentionNeeded(!accessoriesReviewed);
  }, [
    activeSelectedSystem,
    selectedSystems,
    activeSystemType,
    quotableSystems,
    system.uniqueId
  ]);

  const { refs, floatingStyles, context } = useFloating({
    open: showPreview,
    onOpenChange: setShowPreview,
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      shift({ padding: 5 }),
      flip({ fallbackAxisSideDirection: 'end' })
    ]
  });

  const hover = useHover(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const handleClick = () => {
    dispatch(
      setActiveSelectedSystem(
        selectedSystems.findIndex((sys) => sys.uniqueId === system.uniqueId)
      )
    );
    dispatch(setActiveSystemType('ductless'));
  };

  const deleteSelf = async () => {
    const systemIndex = selectedSystems.findIndex(
      (s) => s.uniqueId === system.uniqueId
    );

    try {
      await deleteQuotableSystem(system.uniqueId).unwrap();
    } catch (error) {
      console.error('Failed to delete quotable system:', error);
    }

    if (activeSelectedSystem >= systemIndex) {
      dispatch(setActiveSelectedSystem(Math.max(0, activeSelectedSystem - 1)));
    }

    dispatch(
      setSelectedSystems(
        produce(selectedSystems, (draft) =>
          draft.filter((s) => s.uniqueId !== system.uniqueId)
        )
      )
    );
  };

  const handleDeleteClick = (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    event.stopPropagation();
    setShowPreview(false);
    if (
      isAccessoryPage ||
      system.selectedAccessories.some((acc) => acc.quantity > 0)
    ) {
      confirmationModal(
        'Are you sure you want to delete this system?',
        deleteSelf
      );
    } else {
      deleteSelf();
    }
  };

  const nameIndexes = useMemo(
    () => ductedDuplicateNameIndexes(selectedSystems),
    [selectedSystems]
  );
  const systemLabel = nameWithDuplicateIndex(
    system.name,
    isAccessoryPage ? nameIndexes.get(system.id) : undefined
  );
  const showConfigurationName =
    !isAccessoryPage || showsAccessoryChipConfigurationName(system);

  const classes = clsx(
    'inline-flex items-center rounded-full pl-2 shadow ring-1 ring-black ring-opacity-5 whitespace-nowrap relative',
    isAccessoryPage && active && 'bg-blue-100',
    isAccessoryPage && !active && 'bg-white hover:bg-gray-50',
    !isAccessoryPage && 'bg-white'
  );

  return (
    <div ref={refs.setReference} {...getReferenceProps()}>
      <div
        className={classes}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {!hideDeletable && !isQuoted && (!isAccessoryPage || isHovering) && (
          <XButton
            onClick={(e) => handleDeleteClick(e)}
            tooltip='Delete Selected System'
            onDisabledClick={() =>
              warningModal('You must have at least one selected system.')
            }
            disabled={
              isAccessoryPage &&
              selectedSystems.length + selectedDuctedSystems.length <= 1
            }
          />
        )}
        {isAccessoryPage && (!isHovering || hideDeletable || isQuoted) && (
          <ProgressIndicator size='sm' checked={!attentionNeeded} />
        )}
        {renaming && !hideRenamable ? (
          <div className='relative py-1 pl-1 pr-2'>
            <Text
              size='sm'
              onInput={(value) =>
                setNewName((value ?? '').slice(0, renameSizeLimit))
              }
              value={newName}
              onBlur={() => handleSaveName()}
              onKeyDown={(e) => handleRenameKeyDown(e.key)}
              autoFocus
              autoSelect
              controlled
            />
          </div>
        ) : (
          <button
            type='button'
            className={`inline-flex items-center gap-2 py-2 pl-2 pr-3 ${
              isAccessoryPage ? 'cursor-pointer' : 'cursor-help'
            }`}
            onClick={isAccessoryPage ? handleClick : undefined}
            onDoubleClick={!hideRenamable ? handleDoubleClickName : undefined}
          >
            <span className='text-sm font-semibold'>{systemLabel}</span>
            {showConfigurationName && (
              <span className='text-xs text-gray-500'>
                {system.configuration.name}
              </span>
            )}
            {!isAccessoryPage && (
              <span className='text-sm font-semibold text-green-600'>
                ${system.price}
              </span>
            )}
          </button>
        )}
      </div>
      {showPreview && (
        <div
          ref={refs.setFloating}
          className='z-20 max-w-lg'
          style={floatingStyles}
          {...getFloatingProps()}
        >
          <div className='flex flex-col gap-2 rounded-lg bg-white p-5 shadow-xl'>
            {system.configuration.condensers.map((condenser) => (
              <SystemMap
                key={condenser.uniqueId}
                condenser={condenser}
                showTags
                showSpaces
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
